import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';

export default function About() {
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.elements.namedItem('phone_alt') as HTMLInputElement)?.value.trim();
    if (honeypot) {
      setFormStatus('success');
      return;
    }

    setFormStatus('loading');
    setFormError('');

    try {
      const formData = new FormData(form);
      const res = await fetch('https://readdy.ai/api/form/d96a22v0d76aer3t51qg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await res.text();
      let parsed: Record<string, unknown> = {};
      try { parsed = JSON.parse(responseText); } catch { /* ignore */ }
      if (res.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const msg = (parsed?.meta as Record<string, string>)?.message || responseText || '提交失败，请稍后重试';
        if (msg.toLowerCase().includes('spam')) {
          setFormError('提交失败，请稍后重试');
        } else {
          setFormError(msg);
        }
        setFormStatus('error');
      }
    } catch {
      setFormStatus('error');
      setFormError('网络异常，请稍后重试');
    }
  };

  const stats = [
    { value: '5000万+', label: '月活跃用户', icon: 'ri-user-line' },
    { value: '10万+', label: '每日爆料量', icon: 'ri-flashlight-line' },
    { value: '200+', label: '合作媒体', icon: 'ri-newspaper-line' },
    { value: '8年', label: '行业深耕', icon: 'ri-history-line' },
  ];

  const values = [
    { title: '真实', desc: '我们坚持每一条爆料的真实性，所有内容均经过多轮核实后才进行发布。', icon: 'ri-shield-check-line' },
    { title: '及时', desc: '7x24小时全天候追踪娱乐八卦热点，确保第一时间为您呈现最新鲜的瓜。', icon: 'ri-timer-flash-line' },
    { title: '深度', desc: '不仅报道表面新闻，更致力于挖掘背后的故事和隐情，做有态度的八卦媒体。', icon: 'ri-search-eye-line' },
    { title: '责任', desc: '作为行业领先的爆料平台，我们对每一篇内容负责，坚持合法合规运营。', icon: 'ri-heart-pulse-line' },
  ];

  const orgJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '51黑料网',
    url: 'https://www.51heiliao.com',
    description: '51黑料网是中国领先的娱乐八卦与网红爆料资讯平台，致力于为用户提供真实、及时、有深度的娱乐爆料内容。',
    foundingDate: '2018',
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'business cooperation',
      telephone: '+86-400-888-8888',
      email: 'business@51heiliao.com',
    },
    sameAs: [
      'https://weibo.com/51heiliao',
      'https://www.zhihu.com/topic/51heiliao',
    ],
  };

  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      { '@type': 'Question', name: '51黑料网是做什么的？', acceptedAnswer: { '@type': 'Answer', text: '51黑料网是中国领先的娱乐八卦与网红爆料资讯平台，致力于为用户提供真实、及时、有深度的娱乐爆料内容。我们聚合全网最新最热的明星塌房、网红黑料、博主反差等娱乐资讯。' } },
      { '@type': 'Question', name: '如何联系51黑料网进行商务合作？', acceptedAnswer: { '@type': 'Answer', text: '您可以通过本页面下方的商务合作表单提交合作需求，或发送邮件至 business@51heiliao.com。我们的商务团队将在1-3个工作日内与您联系。' } },
      { '@type': 'Question', name: '51黑料网的内容是否真实可靠？', acceptedAnswer: { '@type': 'Answer', text: '51黑料网坚持内容真实性原则。所有爆料内容均经过编辑团队的多轮核实，我们会对信息来源进行交叉验证，确保为用户提供可靠的资讯内容。同时，如有不实信息，我们也会及时更正。' } },
      { '@type': 'Question', name: '如何在51黑料网上投稿爆料？', acceptedAnswer: { '@type': 'Answer', text: '您可以通过发送邮件至 tips@51heiliao.com 进行匿名投稿爆料。我们承诺保护爆料人的隐私安全，所有投稿信息将严格保密。爆料一经核实采纳，将根据内容价值给予相应稿酬。' } },
    ],
  };

  return (
    <>
      <Navbar />
      <main className="min-h-screen bg-background-50 pt-20">
        <script type="application/ld+json">{JSON.stringify(orgJsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>

        <div className="w-full px-4 md:px-6">
          <div className="py-6 max-w-6xl mx-auto">
            <Breadcrumb items={[{ label: '首页', href: '/' }, { label: '关于我们' }]} />
          </div>

          <section className="max-w-6xl mx-auto mb-20">
            <div className="text-center mb-12">
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground-950 mb-4">
                关于 <span className="text-accent-600">51黑料网</span>
              </h1>
              <p className="text-foreground-500 max-w-2xl mx-auto leading-relaxed">
                中国领先的娱乐八卦与网红爆料资讯平台，致力于还原最真实的娱乐圈生态
              </p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
              {stats.map((stat) => (
                <div key={stat.label} className="bg-background-50 border border-background-200 rounded-xl p-5 md:p-6 text-center hover:border-accent-300 transition-all duration-300">
                  <div className="w-10 h-10 mx-auto mb-3 bg-accent-100 rounded-full flex items-center justify-center">
                    <i className={`${stat.icon} text-accent-600 text-lg`}></i>
                  </div>
                  <div className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-1">{stat.value}</div>
                  <div className="text-sm text-foreground-500">{stat.label}</div>
                </div>
              ))}
            </div>
          </section>

          <section className="max-w-6xl mx-auto mb-20">
            <div className="flex flex-col lg:flex-row gap-10 lg:gap-16 items-center">
              <div className="flex-1">
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-6">
                  我们的 <span className="text-accent-600">故事</span>
                </h2>
                <div className="space-y-4 text-foreground-600 leading-relaxed">
                  <p>
                    51黑料网成立于2018年，由一群热爱娱乐八卦、追求真相的媒体人共同创立。从最初的一个小众爆料论坛开始，经过8年的发展，如今已经成为中国最具影响力的娱乐爆料资讯平台之一。
                  </p>
                  <p>
                    我们深信，娱乐圈不仅是光鲜亮丽的舞台，更是人情冷暖、利益纠葛的缩影。每一篇文章背后，都有我们编辑团队对事实的执着追求和对内容的精心打磨。
                  </p>
                  <p>
                    截至目前，51黑料网已累计发布超过100万篇原创爆料内容，拥有超过5000万的月活跃用户，合作的媒体机构超过200家。我们的目标是将51黑料网打造成为华语世界最值得信赖的娱乐资讯平台。
                  </p>
                </div>
              </div>
              <div className="flex-1">
                <div className="rounded-2xl overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=Modern%20media%20newsroom%20with%20editorial%20team%20collaborating%2C%20creative%20professional%20workspace%20with%20large%20screens%20displaying%20entertainment%20news%2C%20warm%20ambient%20lighting%2C%20clean%20contemporary%20office%20design%2C%20energetic%20yet%20focused%20atmosphere%2C%20editorial%20photography%20style%20with%20natural%20light%20from%20windows%2C%20professional%20media%20company%20setting&width=700&height=500&seq=about-story-01&orientation=landscape"
                    alt="51黑料网办公室"
                    className="w-full h-auto"
                  />
                </div>
              </div>
            </div>
          </section>

          <section className="max-w-6xl mx-auto mb-20">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 text-center mb-10">
              我们的 <span className="text-accent-600">价值观</span>
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
              {values.map((v) => (
                <div key={v.title} className="bg-background-50 border border-background-200 rounded-xl p-6 hover:border-accent-300 transition-all duration-300">
                  <div className="w-12 h-12 bg-accent-100 rounded-xl flex items-center justify-center mb-4">
                    <i className={`${v.icon} text-accent-600 text-xl`}></i>
                  </div>
                  <h3 className="text-lg font-heading font-bold text-foreground-950 mb-2">{v.title}</h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">{v.desc}</p>
                </div>
              ))}
            </div>
          </section>

          <section className="max-w-6xl mx-auto mb-20">
            <div className="bg-background-100 rounded-2xl p-8 md:p-12">
              <div className="text-center mb-8">
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-3">
                  商务合作
                </h2>
                <p className="text-foreground-500 max-w-xl mx-auto">
                  如果您有广告投放、内容合作、品牌推广等需求，欢迎通过以下方式联系我们
                </p>
              </div>

              <div className="flex flex-col lg:flex-row gap-8">
                <div className="flex-1 space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-accent-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-mail-line text-accent-600"></i>
                    </div>
                    <div>
                      <h4 className="font-semibold text-foreground-900 mb-1">商务邮箱</h4>
                      <p className="text-sm text-foreground-500">business@51heiliao.com</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-accent-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-phone-line text-accent-600"></i>
                    </div>
                    <div>
                      <h4 className="font-semibold text-foreground-900 mb-1">商务电话</h4>
                      <p className="text-sm text-foreground-500">400-888-8888</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-accent-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-map-pin-line text-accent-600"></i>
                    </div>
                    <div>
                      <h4 className="font-semibold text-foreground-900 mb-1">公司地址</h4>
                      <p className="text-sm text-foreground-500">北京市朝阳区望京SOHO T3栋 26层</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-accent-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-lightbulb-flash-line text-accent-600"></i>
                    </div>
                    <div>
                      <h4 className="font-semibold text-foreground-900 mb-1">爆料投稿</h4>
                      <p className="text-sm text-foreground-500">tips@51heiliao.com</p>
                    </div>
                  </div>
                </div>

                <div className="flex-1">
                  <form
                    data-readdy-form
                    id="contact-form-d96a22v0"
                    onSubmit={handleSubmit}
                    className="bg-background-50 rounded-xl p-6 border border-background-200 space-y-4"
                  >
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="contact_name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                          姓名 <span className="text-red-500">*</span>
                        </label>
                        <input
                          id="contact_name"
                          name="name"
                          type="text"
                          required
                          className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-foreground-900 text-sm focus:outline-none focus:ring-2 focus:ring-accent-400 focus:border-transparent transition-all"
                          placeholder="请输入您的姓名"
                        />
                      </div>
                      <div>
                        <label htmlFor="contact_email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                          邮箱 <span className="text-red-500">*</span>
                        </label>
                        <input
                          id="contact_email"
                          name="email"
                          type="email"
                          required
                          className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-foreground-900 text-sm focus:outline-none focus:ring-2 focus:ring-accent-400 focus:border-transparent transition-all"
                          placeholder="请输入您的邮箱"
                        />
                      </div>
                    </div>
                    <div>
                      <label htmlFor="contact_company" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        公司/品牌名称
                      </label>
                      <input
                        id="contact_company"
                        name="company"
                        type="text"
                        className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-foreground-900 text-sm focus:outline-none focus:ring-2 focus:ring-accent-400 focus:border-transparent transition-all"
                        placeholder="请输入您的公司或品牌名称"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact_type" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        合作类型 <span className="text-red-500">*</span>
                      </label>
                      <select
                        id="contact_type"
                        name="cooperation_type"
                        required
                        className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-foreground-900 text-sm focus:outline-none focus:ring-2 focus:ring-accent-400 focus:border-transparent transition-all"
                      >
                        <option value="">请选择合作类型</option>
                        <option value="ad">广告投放</option>
                        <option value="content">内容合作</option>
                        <option value="brand">品牌推广</option>
                        <option value="media">媒体合作</option>
                        <option value="other">其他合作</option>
                      </select>
                    </div>
                    <div>
                      <label htmlFor="contact_message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        合作需求描述 <span className="text-red-500">*</span>
                      </label>
                      <textarea
                        id="contact_message"
                        name="message"
                        required
                        maxLength={500}
                        rows={4}
                        className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-foreground-900 text-sm focus:outline-none focus:ring-2 focus:ring-accent-400 focus:border-transparent transition-all resize-none"
                        placeholder="请描述您的合作需求（500字以内）"
                      ></textarea>
                      <p className="text-xs text-foreground-400 mt-1">最多500字符</p>
                    </div>
                    <input
                      type="text"
                      name="phone_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                      className="honeypot-field-contact"
                    />
                    <button
                      type="submit"
                      disabled={formStatus === 'loading'}
                      className="w-full py-3 bg-accent-500 text-foreground-950 font-semibold rounded-lg text-sm hover:bg-accent-600 disabled:opacity-60 disabled:cursor-not-allowed transition-colors cursor-pointer whitespace-nowrap"
                    >
                      {formStatus === 'loading' ? '提交中...' : '提交合作需求'}
                    </button>
                    {formStatus === 'success' && (
                      <p className="text-sm text-green-600 text-center">提交成功！我们将在1-3个工作日内与您联系。</p>
                    )}
                    {formStatus === 'error' && (
                      <p className="text-sm text-red-500 text-center">{formError}</p>
                    )}
                  </form>
                </div>
              </div>
            </div>
          </section>
        </div>
      </main>
      <Footer />
    </>
  );
}